import type { EventDto } from '@pietrochip/shared';
import { createEventSchema, updateEventSchema } from '@pietrochip/shared';
import { useState } from 'react';
import * as api from '../api/endpoints';
import { describeError } from '../api/client';
import { Field, Modal, Notice } from './ui';

/**
 * Creazione e rinomina di un evento.
 *
 * Le due operazioni condividono la finestra perché condividono i campi; quello
 * che cambia è quali sono obbligatori e cosa si dice al server. La validazione
 * usa lo **stesso schema zod del server**: se una regola cambia là, il modulo
 * qui si adegua da solo invece di lasciar partire una richiesta che verrà
 * comunque rifiutata.
 */
export function EventFormModal(
  props:
    | { mode: 'create'; onClose: () => void; onSaved: (event: EventDto) => void }
    | { mode: 'rename'; event: EventDto; onClose: () => void; onSaved: (event: EventDto) => void },
) {
  const { mode, onClose, onSaved } = props;
  const existing = mode === 'rename' ? props.event : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [date, setDate] = useState(existing?.date ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (busy) return;

    const payload = { name, date: date === '' ? null : date, notes };
    const schema = mode === 'create' ? createEventSchema : updateEventSchema;
    const parsed = schema.safeParse(payload);

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Controlla i dati inseriti.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { event } =
        mode === 'create'
          ? await api.createEvent(parsed.data as { name: string })
          : await api.updateEvent(existing!.id, parsed.data);

      onSaved(event);
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={mode === 'create' ? 'Nuovo evento' : 'Rinomina evento'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || name.trim() === ''}
            onClick={() => void onSubmit()}
          >
            {busy ? 'Salvataggio…' : mode === 'create' ? 'Crea evento' : 'Salva'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          void onSubmit();
        }}
      >
        <Field label="Nome dell’evento">
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              autoComplete="off"
              placeholder="Gran Fondo Città di…"
              required
            />
          )}
        </Field>

        <Field label="Data" hint="Facoltativa. Serve a ordinare gli eventi e finisce nel nome del file di riepilogo.">
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              type="date"
              value={date ?? ''}
              onChange={(event) => setDate(event.target.value)}
            />
          )}
        </Field>

        <Field label="Note" hint="Facoltative, solo per chi lavora all’evento.">
          {({ id, ...aria }) => (
            <textarea
              {...aria}
              id={id}
              className="textarea"
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}
        </Field>

        {error && <Notice tone="danger">{error}</Notice>}
      </form>
    </Modal>
  );
}
